/**
 * Checks Korean spelling in a Web Worker, so that loading the dictionary and
 * checking long posts never freezes the page. Falls back to the main thread
 * where workers are not available.
 */

import type { SpellingIssue } from './korean_spellcheck';
import type {
  SpellcheckRequest,
  SpellcheckResponse,
} from './korean_spellcheck_worker';
import { loadWorker } from './workers';

const DICTIONARY_PATH = '/spellcheck';

interface Pending {
  resolve: (issues: SpellingIssue[]) => void;
  reject: (error: Error) => void;
}

let workerPromise: Promise<Worker | null> | null = null;
const pending = new Map<number, Pending>();
let nextId = 1;

function dictionaryBaseUrl() {
  // Absolute, because a worker started from a blob: URL cannot resolve
  // paths relative to the page
  return new URL(DICTIONARY_PATH, window.location.origin).href;
}

function rejectAll(error: Error) {
  pending.forEach(({ reject }) => {
    reject(error);
  });
  pending.clear();
}

async function startWorker(): Promise<Worker | null> {
  try {
    const { default: url } = await import('./korean_spellcheck_worker?worker&url');
    const worker = loadWorker(url, { type: 'module' });

    if (!worker) return null;

    worker.addEventListener(
      'message',
      (event: MessageEvent<SpellcheckResponse>) => {
        const request = pending.get(event.data.id);
        if (!request) return;

        pending.delete(event.data.id);

        if ('error' in event.data) {
          request.reject(new Error(event.data.error));
        } else {
          request.resolve(event.data.issues);
        }
      },
    );

    worker.addEventListener('error', (event) => {
      // A broken worker is started again on the next check
      workerPromise = null;
      worker.terminate();
      rejectAll(new Error(event.message || 'The spellcheck worker failed'));
    });

    return worker;
  } catch (error) {
    console.warn('Could not start the spellcheck worker', error);
    return null;
  }
}

async function checkOnMainThread(text: string) {
  const { checkText } = await import('./korean_spellcheck_engine');

  return checkText(text, dictionaryBaseUrl());
}

/** Returns the misspelled Korean words of a text */
export async function checkKoreanSpelling(
  text: string,
): Promise<SpellingIssue[]> {
  workerPromise ??= startWorker();
  const worker = await workerPromise;

  if (!worker) {
    return checkOnMainThread(text);
  }

  const id = nextId++;

  return new Promise<SpellingIssue[]>((resolve, reject) => {
    pending.set(id, { resolve, reject });
    worker.postMessage({
      id,
      text,
      dictionaryBaseUrl: dictionaryBaseUrl(),
    } satisfies SpellcheckRequest);
  });
}
